import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { knowledgeAPI, completionsAPI, commentsAPI } from '../services/api';
import colors, { shadows, borderRadius, spacing } from '../theme';
import { trackQuizCompletion } from '../utils/analytics';

function KnowledgeDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [knowledge, setKnowledge] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [quizAnswers, setQuizAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [user, setUser] = useState(null);
    const [notionContent, setNotionContent] = useState(null);
    const [loadingNotion, setLoadingNotion] = useState(false);
    const [notionError, setNotionError] = useState(null);
    const [confluenceContent, setConfluenceContent] = useState(null);
    const [loadingConfluence, setLoadingConfluence] = useState(false);
    const [confluenceError, setConfluenceError] = useState(null);
    const [confluenceMetadata, setConfluenceMetadata] = useState(null);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [quizComplete, setQuizComplete] = useState(false);
    const [quizScore, setQuizScore] = useState(null);
    const [quizFeedback, setQuizFeedback] = useState(null);
    const [isPracticeMode, setIsPracticeMode] = useState(false);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        try {
            const [knowledgeRes, commentsRes] = await Promise.all([
                knowledgeAPI.getOne(id),
                commentsAPI.getByKnowledge(id)
            ]);

            const knowledgeData = knowledgeRes.data.data;
            setKnowledge(knowledgeData);
            setComments(commentsRes.data.data);
            setLoading(false);

            // Check if in practice mode (failed quiz within 48 hours)
            if (knowledgeData.user_score !== null && knowledgeData.user_score < 100) {
                const completedDate = new Date(knowledgeData.user_completed_at);
                const hoursSince = (Date.now() - completedDate) / (1000 * 60 * 60);
                if (hoursSince < 48) {
                    setIsPracticeMode(true);
                }
            }

            // If this is a Notion-synced entry, fetch live content
            if (knowledgeData.notion_page_id) {
                fetchNotionContent();
            }

            // If this is a Confluence-synced entry, fetch live content
            if (knowledgeData.confluence_page_id) {
                fetchConfluenceContent();
            }
        } catch (error) {
            console.error('Error loading knowledge:', error);
            setLoading(false);
        }
    };

    const fetchNotionContent = async () => {
        setLoadingNotion(true);
        setNotionError(null);

        try {
            const response = await knowledgeAPI.getNotionContent(id);
            setNotionContent(response.data.data.content);
        } catch (error) {
            console.error('Error loading Notion content:', error);
            setNotionError('Failed to load content from Notion. Please check your Notion integration or try again later.');
        } finally {
            setLoadingNotion(false);
        }
    };

    const fetchConfluenceContent = async () => {
        setLoadingConfluence(true);
        setConfluenceError(null);

        try {
            const response = await knowledgeAPI.getConfluenceContent(id);
            setConfluenceContent(response.data.data.content);
            setConfluenceMetadata(response.data.data.metadata);
        } catch (error) {
            console.error('Error loading Confluence content:', error);
            setConfluenceError('Failed to load content from Confluence. Please check your Confluence integration or try again later.');
        } finally {
            setLoadingConfluence(false);
        }
    };

    const handleAcknowledgment = async () => {
        setSubmitting(true);
        try {
            await completionsAPI.markComplete({
                knowledge_id: parseInt(id),
                type: 'acknowledgment'
            });
            navigate('/dashboard');
        } catch (error) {
            console.error('Error completing:', error);
            alert('Error marking as complete');
            setSubmitting(false);
        }
    };

    const handleQuizSubmit = async () => {
        setSubmitting(true);
        try {
            const questions = knowledge.quiz_questions;
            let score = 0;

            // Convert quizAnswers object to array
            const userAnswersArray = questions.map((_, idx) => quizAnswers[idx]);

            questions.forEach((q, idx) => {
                if (quizAnswers[idx] === q.correctAnswer) {
                    score++;
                }
            });

            setQuizScore({ score, total: questions.length });
            setQuizComplete(true);

            const response = await completionsAPI.markComplete({
                knowledge_id: parseInt(id),
                type: 'quiz',
                score: score,
                total: questions.length,
                user_answers: userAnswersArray,
                is_practice: isPracticeMode
            });

            // Store detailed feedback from backend
            if (response.data.feedback) {
                setQuizFeedback(response.data.feedback);
            }

            // Track quiz completion in Google Analytics
            const passed = score >= questions.length * 0.7;
            const attemptNumber = response.data.data?.attempt_number || 1;
            trackQuizCompletion(knowledge.title, score, questions.length, passed, attemptNumber);

            setSubmitting(false);
        } catch (error) {
            console.error('Error submitting quiz:', error);
            const errorMessage = error.response?.data?.error || 'Error submitting quiz';
            alert(errorMessage);
            setSubmitting(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            await commentsAPI.create({
                knowledge_id: parseInt(id),
                content: newComment
            });
            setNewComment('');
            loadData(); // Refresh to show new comment
        } catch (error) {
            console.error('Error adding comment:', error);
            alert('Error adding comment');
        }
    };

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
    }

    if (!knowledge) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Knowledge not found</div>;
    }

    const quizQuestions = knowledge.quiz_questions || [];

    return (
        <div style={{ minHeight: '100vh', backgroundColor: colors.gray[100] }}>
            {/* Header */}

            {/* Content */}
            <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                <div style={{ backgroundColor: colors.white, padding: '2rem', borderRadius: '8px', boxShadow: shadows.sm }}>
                    {/* Title */}
                    <h1 style={{ marginBottom: '0.5rem' }}>{knowledge.title}</h1>
                    <p style={{ color: colors.gray[600], marginBottom: '1rem' }}>{knowledge.description}</p>

                    <div style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span>By {knowledge.creator_name} • {new Date(knowledge.created_at).toLocaleDateString()}</span>
                        {knowledge.notion_page_id && (
                            <span style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: colors.accent3,
                                color: colors.white,
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                borderRadius: '4px'
                            }}>
                                🔄 Synced from Notion
                            </span>
                        )}
                        {knowledge.confluence_page_id && (
                            <a
                                href={knowledge.confluence_page_url || confluenceMetadata?.webUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-block',
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: colors.blue[500],
                                    color: colors.white,
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                🌊 View in Confluence →
                            </a>
                        )}
                    </div>

                    {/* Notion error message */}
                    {notionError && (
                        <div style={{
                            backgroundColor: colors.red[50],
                            color: colors.red[600],
                            padding: '0.75rem',
                            borderRadius: '6px',
                            marginBottom: '1rem',
                            fontSize: '0.875rem'
                        }}>
                            ⚠️ {notionError}
                        </div>
                    )}

                    {/* Confluence error message */}
                    {confluenceError && (
                        <div style={{
                            backgroundColor: colors.red[50],
                            color: colors.red[600],
                            padding: '0.75rem',
                            borderRadius: '6px',
                            marginBottom: '1rem',
                            fontSize: '0.875rem'
                        }}>
                            ⚠️ {confluenceError}
                        </div>
                    )}

                    {/* Content */}
                    <div style={{
                        backgroundColor: colors.gray[50],
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '2rem',
                        whiteSpace: 'pre-wrap',
                        position: 'relative'
                    }}>
                        {loadingNotion ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray[600] }}>
                                Loading live content from Notion...
                            </div>
                        ) : loadingConfluence ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray[600] }}>
                                Loading live content from Confluence...
                            </div>
                        ) : (
                            notionContent || confluenceContent || knowledge.content
                        )}

                        {/* Confluence metadata display */}
                        {confluenceMetadata && confluenceContent && (
                            <div style={{
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: `1px solid ${colors.gray[300]}`,
                                fontSize: '0.75rem',
                                color: colors.gray[600]
                            }}>
                                Last updated in Confluence: {new Date(confluenceMetadata.lastModified).toLocaleString()} • Version {confluenceMetadata.version}
                            </div>
                        )}
                    </div>

                    {/* Resources */}
                    {knowledge.resources && (
                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>📎 Resources</h3>
                            <a
                                href={knowledge.resources}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: colors.blue[500] }}
                            >
                                {knowledge.resources}
                            </a>
                        </div>
                    )}

                    {/* Assessment */}
                    {knowledge.assessment_type === 'acknowledgment' ? (
                        <div style={{ backgroundColor: colors.blue[100], padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
                            <p style={{ marginBottom: '1rem' }}>Have you reviewed and understood this content?</p>
                            <button
                                onClick={handleAcknowledgment}
                                disabled={submitting}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: submitting ? colors.gray[400] : colors.green[500],
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem'
                                }}
                            >
                                {submitting ? 'Submitting...' : 'Yes, I Understand'}
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Practice Mode Banner */}
                            {isPracticeMode && (
                                <div style={{
                                    backgroundColor: colors.orange[100],
                                    border: `2px solid ${colors.orange[700]}`,
                                    borderRadius: borderRadius.lg,
                                    padding: spacing.md,
                                    marginBottom: spacing.lg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: spacing.sm
                                }}>
                                    <span style={{ fontSize: '1.5rem' }}>🔥</span>
                                    <div>
                                        <h3 style={{ margin: 0, color: colors.orange[700], fontSize: '1rem', fontWeight: '600' }}>
                                            Practice Mode
                                        </h3>
                                        <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: colors.gray[700] }}>
                                            This attempt won't count toward your official score. Practice as many times as you need!
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div style={{
                                backgroundColor: colors.blue[50],
                                padding: '2rem',
                                borderRadius: '12px',
                                marginBottom: '2rem',
                                textAlign: 'center',
                                border: `2px solid ${colors.blue[200]}`
                            }}>
                                <h3 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>📝 Ready to test your knowledge?</h3>
                            <p style={{ color: colors.gray[600], marginBottom: '1.5rem' }}>
                                Take the quiz to complete this learning module ({quizQuestions.length} questions)
                            </p>
                            <button
                                onClick={() => setShowQuizModal(true)}
                                style={{
                                    padding: '1rem 2rem',
                                    backgroundColor: colors.blue[500],
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    boxShadow: shadows.md,
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = colors.blue[600];
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = shadows.lg;
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = colors.blue[500];
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = shadows.md;
                                }}
                            >
                                Take Quiz
                            </button>
                            </div>
                        </>
                    )}

                    {/* Comments */}
                    <div style={{ borderTop: `1px solid ${colors.gray[200]}`, paddingTop: '2rem' }}>
                        <h3 style={{ marginBottom: '1rem' }}>💬 Comments ({comments.length})</h3>

                        {comments.map((comment) => (
                            <div key={comment.id} style={{
                                backgroundColor: colors.gray[50],
                                padding: '1rem',
                                borderRadius: '8px',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{comment.user_name}</span>
                                    <span style={{ fontSize: '0.75rem', color: colors.gray[600] }}>
                                        {new Date(comment.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem' }}>{comment.content}</p>
                            </div>
                        ))}

                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                            <input
                                type="text"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Add a comment..."
                                style={{
                                    flex: 1,
                                    padding: '0.5rem',
                                    border: `1px solid ${colors.gray[300]}`,
                                    borderRadius: '4px'
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddComment();
                                    }
                                }}
                            />
                            <button
                                onClick={handleAddComment}
                                disabled={!newComment.trim()}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: !newComment.trim() ? colors.gray[400] : colors.blue[500],
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: !newComment.trim() ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Comment
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quiz Modal */}
            {showQuizModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '2rem'
                }}>
                    <div style={{
                        backgroundColor: colors.white,
                        borderRadius: '16px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }}>
                        {!quizComplete ? (
                            <>
                                {/* Quiz Header */}
                                <div style={{
                                    padding: '2rem',
                                    borderBottom: `2px solid ${colors.gray[200]}`,
                                    backgroundColor: colors.blue[50]
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>📝 Knowledge Quiz</h2>
                                        <span style={{
                                            backgroundColor: colors.blue[500],
                                            color: colors.white,
                                            padding: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            fontSize: '0.9rem',
                                            fontWeight: '600'
                                        }}>
                                            Question {currentQuestion + 1} of {quizQuestions.length}
                                        </span>
                                    </div>
                                    {/* Progress Bar */}
                                    <div style={{
                                        backgroundColor: colors.gray[200],
                                        height: '8px',
                                        borderRadius: '4px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            backgroundColor: colors.blue[500],
                                            height: '100%',
                                            width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%`,
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                </div>

                                {/* Practice Mode Warning in Modal */}
                                {isPracticeMode && (
                                    <div style={{
                                        backgroundColor: colors.orange[100],
                                        border: `2px solid ${colors.orange[700]}`,
                                        borderLeft: 'none',
                                        borderRight: 'none',
                                        padding: spacing.sm,
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        color: colors.orange[700]
                                    }}>
                                        🔥 PRACTICE MODE - This attempt won't count toward your official score
                                    </div>
                                )}

                                {/* Question */}
                                <div style={{ padding: '2.5rem' }}>
                                    <h3 style={{
                                        fontSize: '1.3rem',
                                        marginBottom: '2rem',
                                        lineHeight: '1.6'
                                    }}>
                                        {quizQuestions[currentQuestion].question}
                                    </h3>

                                    {/* Answers */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {quizQuestions[currentQuestion].answers.map((answer, aIdx) => (
                                            <label
                                                key={aIdx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '1.25rem',
                                                    border: `2px solid ${quizAnswers[currentQuestion] === aIdx ? colors.blue[500] : colors.gray[300]}`,
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    backgroundColor: quizAnswers[currentQuestion] === aIdx ? colors.blue[50] : colors.white,
                                                    transition: 'all 0.2s',
                                                    fontSize: '1.05rem'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (quizAnswers[currentQuestion] !== aIdx) {
                                                        e.currentTarget.style.backgroundColor = colors.gray[50];
                                                        e.currentTarget.style.borderColor = colors.gray[400];
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    if (quizAnswers[currentQuestion] !== aIdx) {
                                                        e.currentTarget.style.backgroundColor = colors.white;
                                                        e.currentTarget.style.borderColor = colors.gray[300];
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${currentQuestion}`}
                                                    checked={quizAnswers[currentQuestion] === aIdx}
                                                    onChange={() => setQuizAnswers({ ...quizAnswers, [currentQuestion]: aIdx })}
                                                    style={{ marginRight: '1rem', width: '20px', height: '20px' }}
                                                />
                                                <span>{answer}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div style={{
                                    padding: '1.5rem 2rem',
                                    borderTop: `1px solid ${colors.gray[200]}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '1rem'
                                }}>
                                    <button
                                        onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                                        disabled={currentQuestion === 0}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: currentQuestion === 0 ? colors.gray[200] : colors.gray[100],
                                            color: currentQuestion === 0 ? colors.gray[400] : colors.gray[700],
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ← Previous
                                    </button>

                                    {currentQuestion === quizQuestions.length - 1 ? (
                                        <button
                                            onClick={handleQuizSubmit}
                                            disabled={submitting || Object.keys(quizAnswers).length !== quizQuestions.length}
                                            style={{
                                                padding: '0.75rem 2rem',
                                                backgroundColor: submitting || Object.keys(quizAnswers).length !== quizQuestions.length ? colors.gray[400] : colors.green[500],
                                                color: colors.white,
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: submitting || Object.keys(quizAnswers).length !== quizQuestions.length ? 'not-allowed' : 'pointer',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Quiz ✓'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setCurrentQuestion(currentQuestion + 1)}
                                            disabled={quizAnswers[currentQuestion] === undefined}
                                            style={{
                                                padding: '0.75rem 2rem',
                                                backgroundColor: quizAnswers[currentQuestion] === undefined ? colors.gray[400] : colors.blue[500],
                                                color: colors.white,
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: quizAnswers[currentQuestion] === undefined ? 'not-allowed' : 'pointer',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Next →
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            /* Quiz Complete - Results with Detailed Feedback */
                            <div style={{ padding: '2rem' }}>
                                {quizScore ? (
                                    <>
                                        {/* Practice Mode Indicator */}
                                        {isPracticeMode && (
                                            <div style={{
                                                backgroundColor: colors.orange[100],
                                                padding: spacing.sm,
                                                borderRadius: borderRadius.md,
                                                marginBottom: spacing.md,
                                                textAlign: 'center',
                                                color: colors.orange[700]
                                            }}>
                                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                    🔥 PRACTICE ATTEMPT - This doesn't count toward your official score
                                                </div>
                                                {knowledge && knowledge.user_completed_at && (
                                                    <div style={{ fontSize: '0.875rem' }}>
                                                        {(() => {
                                                            const completedDate = new Date(knowledge.user_completed_at);
                                                            const hoursSince = (Date.now() - completedDate) / (1000 * 60 * 60);
                                                            const hoursLeft = Math.ceil(48 - hoursSince);
                                                            return hoursLeft > 0
                                                                ? `Your next official attempt will count in ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}`
                                                                : 'Your next attempt will count toward your official score!';
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Score Summary */}
                                        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                                            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
                                                {quizScore.score >= quizScore.total * 0.7 ? '🎉' : '📚'}
                                            </div>
                                            <h2 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>
                                                {quizScore.score >= quizScore.total * 0.7 ? 'Great Job!' : 'Keep Learning!'}
                                            </h2>
                                            <p style={{
                                                fontSize: '1.25rem',
                                                color: colors.gray[700],
                                                marginBottom: '0.25rem'
                                            }}>
                                                Score: <strong style={{ color: quizScore.score >= quizScore.total * 0.7 ? colors.green[600] : colors.red[700] }}>
                                                    {quizScore.score}/{quizScore.total}
                                                </strong> ({Math.round((quizScore.score / quizScore.total) * 100)}%)
                                            </p>
                                        </div>

                                {/* Detailed Feedback */}
                                {quizFeedback && (
                                    <div style={{ marginBottom: '2rem' }}>
                                        <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: colors.gray[700] }}>
                                            Review Your Answers
                                        </h3>
                                        {quizFeedback.map((item, idx) => (
                                            <div key={idx} style={{
                                                marginBottom: '1.5rem',
                                                padding: '1.25rem',
                                                backgroundColor: item.isCorrect ? colors.green[50] : colors.red[50],
                                                borderRadius: borderRadius.md,
                                                border: `2px solid ${item.isCorrect ? colors.green[200] : colors.red[200]}`
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '0.75rem'
                                                }}>
                                                    <span style={{
                                                        fontSize: '1.5rem',
                                                        marginRight: '0.5rem'
                                                    }}>
                                                        {item.isCorrect ? '✅' : '❌'}
                                                    </span>
                                                    <strong style={{
                                                        color: colors.gray[800],
                                                        fontSize: '1rem'
                                                    }}>
                                                        Question {idx + 1}
                                                    </strong>
                                                </div>
                                                <p style={{
                                                    marginBottom: '0.75rem',
                                                    color: colors.gray[700],
                                                    fontSize: '0.95rem'
                                                }}>
                                                    {item.question}
                                                </p>
                                                <div style={{ fontSize: '0.9rem' }}>
                                                    {!item.isCorrect && (
                                                        <p style={{
                                                            color: colors.red[700],
                                                            marginBottom: '0.5rem'
                                                        }}>
                                                            <strong>Your answer:</strong> {item.answers[item.userAnswer]}
                                                        </p>
                                                    )}
                                                    <p style={{
                                                        color: colors.green[700],
                                                        fontWeight: '600'
                                                    }}>
                                                        <strong>Correct answer:</strong> {item.answers[item.correctAnswer]}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                        {/* Action Buttons */}
                                        <div style={{ textAlign: 'center' }}>
                                            {quizScore.score < quizScore.total && (
                                                <p style={{
                                                    color: quizScore.score < quizScore.total * 0.7 ? colors.red[700] : colors.blue[700],
                                                    marginBottom: '1rem',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    💡 {quizScore.score < quizScore.total * 0.7
                                                        ? 'Review the material above and retake the quiz to improve your score.'
                                                        : 'Great job! Want to try for a perfect score?'}
                                                </p>
                                            )}
                                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <button
                                                    onClick={() => {
                                                        setQuizComplete(false);
                                                        setQuizScore(null);
                                                        setQuizFeedback(null);
                                                        setQuizAnswers({});
                                                        setCurrentQuestion(0);
                                                    }}
                                                    style={{
                                                        padding: '0.875rem 2rem',
                                                        backgroundColor: colors.white,
                                                        color: colors.blue[600],
                                                        border: `2px solid ${colors.blue[500]}`,
                                                        borderRadius: borderRadius.md,
                                                        cursor: 'pointer',
                                                        fontSize: '1rem',
                                                        fontWeight: '600',
                                                        boxShadow: shadows.sm
                                                    }}
                                                >
                                                    🔄 Retake Quiz
                                                </button>
                                                <button
                                                    onClick={() => navigate('/dashboard')}
                                                    style={{
                                                        padding: '0.875rem 2rem',
                                                        backgroundColor: colors.blue[500],
                                                        color: colors.white,
                                                        border: 'none',
                                                        borderRadius: borderRadius.md,
                                                        cursor: 'pointer',
                                                        fontSize: '1rem',
                                                        fontWeight: '600',
                                                        boxShadow: shadows.md
                                                    }}
                                                >
                                                    Return to Dashboard
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    /* Fallback if quiz score is missing */
                                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                                        <p style={{ color: colors.gray[600], marginBottom: '1rem' }}>
                                            Quiz submitted successfully!
                                        </p>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            style={{
                                                padding: '0.875rem 2rem',
                                                backgroundColor: colors.blue[500],
                                                color: colors.white,
                                                border: 'none',
                                                borderRadius: borderRadius.md,
                                                cursor: 'pointer',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                boxShadow: shadows.md
                                            }}
                                        >
                                            Return to Dashboard
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default KnowledgeDetailPage;